import React, { useState, useEffect } from "react";
import { HiX, HiClipboardCopy } from "react-icons/hi";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE = "https://app.caaficare.so/api";

// USSD code: use merchant_number from payment method as-is, add amount after last *
// e.g. *712*622222154* -> *712*622222154*4.00#
const getUssdCode = (method, amount) => {
  const raw = (method.merchant_number || "").trim();
  if (!raw) return "";
  const amt = String(amount || "0").replace(/[$,]/g, "").trim() || "0";
  const base = raw.replace(/\*+$/, ""); // remove trailing asterisks
  return `${base}*${amt}#`;
};

const PaymentModal = ({ isOpen, onClose, appointmentPrice, onPaymentComplete }) => {
  const [selectedMethodId, setSelectedMethodId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // Fetch payment methods from system API only (no hardcoded numbers)
  useEffect(() => {
    if (!isOpen) return;
    const fetchMethods = async () => {
      setLoadingMethods(true);
      try {
        const res = await axios.get(`${API_BASE}/payment-methods?active=true`);
        if (res.data?.success && res.data.data?.length > 0) {
          setPaymentMethods(res.data.data);
          setSelectedMethodId(res.data.data[0]?.id ?? null);
        } else {
          setPaymentMethods([]);
          setSelectedMethodId(null);
        }
      } catch (err) {
        console.error("Error fetching payment methods:", err);
        setPaymentMethods([]);
        setSelectedMethodId(null);
      } finally {
        setLoadingMethods(false);
      }
    };
    fetchMethods();
  }, [isOpen]);

  const selectedMethod = paymentMethods.find((m) => m.id === selectedMethodId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-[2.5rem] w-full max-w-4xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-6 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-black text-white tracking-tight">
              Complete Payment
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Choose your preferred payment method
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-all"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[80vh]">
          {/* Amount Display */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 mb-8 border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-1">
                  Total Amount
                </p>
                <p className="text-4xl font-black text-blue-900">
                  ${appointmentPrice || "0"}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Methods - Image Grid Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-black text-gray-900 mb-4">
              Select Payment Method
            </h3>

            {loadingMethods ? (
              <div className="text-center py-8 text-gray-500">Loading payment methods...</div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No payment methods available</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {paymentMethods.map((method) => {
                    const isSelected = selectedMethodId === method.id;
                    const imageSrc = method.image?.startsWith("http") ? method.image : (method.image ? method.image : null);

                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setSelectedMethodId(isSelected ? null : method.id)}
                        className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all cursor-pointer bg-white hover:border-blue-300 ${
                          isSelected
                            ? "border-blue-500 ring-2 ring-blue-200 shadow-lg shadow-blue-100"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden mb-3 p-2">
                          {imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={method.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling?.classList.remove("hidden");
                              }}
                            />
                          ) : null}
                          <span className={`text-2xl font-bold text-blue-600 ${imageSrc ? "hidden" : ""}`}>
                            {method.name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-900 text-center">
                          {method.name}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {selectedMethod && getUssdCode(selectedMethod, appointmentPrice) && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-2xl border-2 border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-2">
                      USSD code for {selectedMethod.name} (with amount):
                    </p>
                    <div className="flex items-center gap-3">
                      <code className="flex-1 text-sm font-mono font-bold text-gray-900 break-all bg-white px-3 py-2 rounded-lg border border-gray-200">
                        {getUssdCode(selectedMethod, appointmentPrice)}
                      </code>
                      <button
                        type="button"
                        onClick={() => {
                          const code = getUssdCode(selectedMethod, appointmentPrice);
                          navigator.clipboard?.writeText(code);
                          toast.success("USSD code with amount copied!");
                        }}
                        className="flex-shrink-0 p-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        title="Copy USSD code with amount"
                      >
                        <HiClipboardCopy className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">Dial this code or copy to send payment</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-4 rounded-2xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!onPaymentComplete) {
                  onClose();
                  return;
                }
                setIsCompleting(true);
                try {
                  const result = await onPaymentComplete(selectedMethod?.name || null);
                  if (result !== false) onClose();
                } finally {
                  setIsCompleting(false);
                }
              }}
              disabled={!selectedMethodId || loadingMethods || isCompleting}
              className="flex-1 px-6 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 text-white font-black hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCompleting ? (
                <>
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                "I've Paid"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;